import React from 'react'
import { Dropdown } from 'antd'
import classNames from 'classnames'
import i18n from 'i18next'
import { DO_NOT_IMPORT, FIELD_TYPE_CONFIG, getFieldTypeOptions } from './importReducer'
import {
  GOOGLE_COLUMN_MAP,
  createCsvColumnTranslator,
  getDuplicateColumns,
  getUnmappedColumns,
} from './importUtils'
import UnmappedColumnsNotice from './UnmappedColumnsNotice'
import lockIconSvg from '@/assets/images/icon-lock.svg'
import styles from './style.module.scss'

// v2.11: Dropdown menu의 selectedKeys 매칭용 sentinel — 빈 문자열은 key로 부적합
const SKIP_SENTINEL = '__skip__'

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M6.5 1v11M1 6.5h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

// v2.7: CSV 컬럼 드롭다운용 화살표 아이콘 (addFieldBtn과 동일한 Dropdown 패턴에 사용)
const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M3 4.5l3 3 3-3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const TrashIcon = () => (
  <svg width="13" height="14" viewBox="0 0 13 14" fill="none">
    <path
      d="M1 3.5h11M4.5 3.5V2.5a1 1 0 011-1h2a1 1 0 011 1v1M5.5 6.5v4M7.5 6.5v4M2 3.5l.7 8a1 1 0 001 .9h5.6a1 1 0 001-.9l.7-8"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const PencilIcon = () => (
  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path
      d="M9.5 2.5l2 2L4 12H2v-2l7.5-7.5z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CheckIcon = () => (
  <svg width="12" height="10" viewBox="0 0 13 10" fill="none">
    <path
      d="M1 5l4 4 7-8"
      stroke="currentColor"
      strokeWidth="1.8"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const LockIcon = () => <img src={lockIconSvg} alt="secure" width="12" height="12" />

// 프리셋마다 안내 문구가 다르다 — 호출마다 키를 리터럴로 써 번역 키 추출이 따라가게 한다
const getMappingDescription = (presetId, t) => {
  if (presetId === 'google') return t('record.import.v2.mapping.desc.google')
  if (presetId === 'template') return t('record.import.v2.mapping.desc.template')
  return t('record.import.v2.mapping.desc.custom')
}

const ImportMappingStep = ({
  preset,
  mappings,
  csvColumns,
  translations,
  dispatch,
  savedAsTemplateName = null,
  t,
}) => {
  const isGoogle = preset.id === 'google'
  const dupCols = getDuplicateColumns(mappings)
  const unmappedColumns = getUnmappedColumns(csvColumns, mappings)

  // v2.12: CSV 컬럼명을 템플릿 translations 로 현재 언어에 맞춰 표시.
  // 매칭 실패 시 원본 그대로 (사용자 임의 컬럼명 보존).
  const locale = (i18n.language || 'ko-KR').replace('-', '_')
  const columnMap = isGoogle ? GOOGLE_COLUMN_MAP : null
  const translateCol = createCsvColumnTranslator(translations, locale, columnMap)

  const handleColChange = (id, col) => {
    dispatch({ type: 'UPDATE_MAPPING_COL', payload: { id, col } })
  }

  const handleAdd = (type) => {
    dispatch({ type: 'ADD_MAPPING_FIELD', payload: { type } })
  }

  const handleAddColumnAsField = (col, type) => {
    dispatch({ type: 'ADD_MAPPING_FIELD_FOR_COLUMN', payload: { col, type } })
  }

  const handleRemove = (id) => {
    dispatch({ type: 'REMOVE_MAPPING_FIELD', payload: id })
  }

  // v2.3 § 24: 커스텀 필드 라벨 편집 (연필 클릭 → 편집, ✓ 클릭 → 저장)
  // v2.4: 편집 모드 진입 시 기본값은 타입의 번역된 라벨. reducer가 번역 함수에
  // 접근할 수 없으므로 컴포넌트에서 defaultLabel을 계산해 payload로 전달한다.
  const handleStartEdit = (id, type) => {
    const config = FIELD_TYPE_CONFIG[type]
    const defaultLabel = config ? t(config.labelKey) : type
    dispatch({ type: 'START_EDIT_CUSTOM_LABEL', payload: { id, defaultLabel } })
  }

  const handleLabelDraftChange = (id, value) => {
    dispatch({ type: 'UPDATE_CUSTOM_LABEL_DRAFT', payload: { id, value } })
  }

  const handleCommitLabel = (id) => {
    dispatch({ type: 'COMMIT_CUSTOM_LABEL', payload: { id } })
  }

  // 필드 타입 Dropdown 메뉴 아이템 (10개 타입) — v2.9: encrypt 필드에 자물쇠 아이콘 + tooltip.
  // '필드 추가' 와 미연결 열의 '필드로 추가' 가 같은 목록을 쓰고 고른 뒤 할 일(onSelect)만 다르다
  const buildFieldTypeMenuItems = (onSelect) =>
    getFieldTypeOptions(t).map((ft) => ({
      key: ft.value,
      label: (
        <div className={styles.fieldTypeMenuItem}>
          <span>{ft.label}</span>
          {ft.encrypt && (
            <span
              className={styles.fieldTypeMenuLock}
              title={t('record.import.v2.mapping.encryptedFieldTooltip')}
            >
              <LockIcon />
            </span>
          )}
        </div>
      ),
      onClick: () => onSelect(ft.value),
    }))

  return (
    <div className={styles.mappingStep}>
      <p className={styles.mappingDesc}>{getMappingDescription(preset.id, t)}</p>
      {savedAsTemplateName && (
        <p className={styles.mappingDesc}>
          {t('record.import.v2.mapping.savedAs', { template: savedAsTemplateName })}
        </p>
      )}

      {unmappedColumns.length > 0 && (
        <UnmappedColumnsNotice
          unmappedColumns={unmappedColumns}
          translateColumn={translateCol}
          buildFieldTypeMenuItems={buildFieldTypeMenuItems}
          onAddAsField={handleAddColumnAsField}
          t={t}
        />
      )}

      <div className={styles.mappingTable}>
        <div className={styles.mappingHeader}>
          <span>{t('record.import.v2.mapping.opField')}</span>
          <span>{t('record.import.v2.mapping.csvColumn')}</span>
          <span />
        </div>

        {mappings.map((m, idx) => {
          const isDup = dupCols.includes(m.col) && m.col !== DO_NOT_IMPORT
          const isLastRow = idx === mappings.length - 1

          // 커스텀 필드의 표시 라벨: customLabel이 있으면 그것, 없으면 번역된 타입 라벨 → type ID 폴백
          const typeConfig = FIELD_TYPE_CONFIG[m.type]
          const displayLabel = m.customLabel || (typeConfig ? t(typeConfig.labelKey) : m.type)
          const canCommit = m.isEditingLabel && (m.labelDraft || '').trim().length > 0

          // 1열 (OfficePassword 필드 셀) 렌더링 분기
          let firstColumn
          if (!m.isCustom) {
            // 기본 필드: 라벨만 표시
            firstColumn = (
              <span className={styles.mappingFieldLabel}>
                {m.field}
                {m.required && <span className={styles.mappingRequired}> *</span>}
              </span>
            )
          } else if (m.isEditingLabel) {
            // 커스텀 필드 — 편집 모드: 입력창 + ✓
            firstColumn = (
              <div className={styles.mappingCustomLabelEdit}>
                <input
                  type="text"
                  className={styles.mappingLabelInput}
                  value={m.labelDraft || ''}
                  onChange={(e) => handleLabelDraftChange(m.id, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canCommit) handleCommitLabel(m.id)
                  }}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <button
                  type="button"
                  className={[
                    styles.mappingLabelActionBtn,
                    !canCommit ? styles.mappingLabelActionBtnDisabled : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleCommitLabel(m.id)}
                  disabled={!canCommit}
                  aria-label="Save"
                >
                  <CheckIcon />
                </button>
              </div>
            )
          } else {
            // 커스텀 필드 — 읽기 모드: "라벨 ✏️"
            firstColumn = (
              <div className={styles.mappingCustomLabelRead}>
                <span className={styles.mappingFieldLabel}>{displayLabel}</span>
                <button
                  type="button"
                  className={styles.mappingLabelActionBtn}
                  onClick={() => handleStartEdit(m.id, m.type)}
                  aria-label="Edit label"
                >
                  <PencilIcon />
                </button>
              </div>
            )
          }

          // v2.10: 중복 발생 행은 에러 메시지(position: absolute)가 다음 행을 침범하지 않도록 padding-bottom 확장.
          const rowClass = [styles.mappingRow, isDup ? styles.mappingRowWithDup : '']
            .filter(Boolean)
            .join(' ')

          return (
            <div
              key={m.id}
              className={rowClass}
              style={{ borderBottom: isLastRow ? 'none' : '1px solid #E2E8F0' }}
            >
              <div className={styles.mappingCells}>
                {/* 1열: OfficePassword 필드 셀 */}
                {firstColumn}

                {/* 2열: CSV 컬럼 Dropdown (v2.11: Select → Dropdown 통일, addFieldBtn 톤) */}
                {/* v2.10: 에러 메시지를 absolute 배치하기 위한 기준점(mappingSelectCell) */}
                <div className={styles.mappingSelectCell}>
                  <Dropdown
                    overlayClassName={classNames('menuStyle2', 'contextMenu')}
                    menu={{
                      items: [
                        {
                          key: SKIP_SENTINEL,
                          label: t('record.import.v2.mapping.doNotImport'),
                          onClick: () => handleColChange(m.id, DO_NOT_IMPORT),
                        },
                        ...csvColumns.map((c) => ({
                          key: c,
                          label: translateCol(c),
                          onClick: () => handleColChange(m.id, c),
                        })),
                      ],
                      selectedKeys: [m.col || SKIP_SENTINEL],
                    }}
                    trigger={['click']}
                    placement="bottomLeft"
                  >
                    <button
                      type="button"
                      className={[styles.mappingSelect, isDup ? styles.mappingSelectError : '']
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <span className={styles.mappingSelectLabel}>
                        {m.col ? translateCol(m.col) : t('record.import.v2.mapping.doNotImport')}
                      </span>
                      <ChevronDownIcon />
                    </button>
                  </Dropdown>
                  {isDup && (
                    <p className={styles.mappingDupError} aria-live="polite">
                      {t('record.import.v2.mapping.dupColumn')}
                    </p>
                  )}
                </div>

                {/* 3열: 삭제 버튼 (커스텀 필드만) */}
                <div className={styles.mappingActions}>
                  {m.isCustom && (
                    <button
                      type="button"
                      className={styles.mappingDeleteBtn}
                      onClick={() => handleRemove(m.id)}
                      aria-label="Delete field"
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* 필드 추가 Dropdown (v2.3 § 25) */}
      <Dropdown
        menu={{ items: buildFieldTypeMenuItems(handleAdd) }}
        trigger={['click']}
        placement="bottomLeft"
        overlayClassName="contextMenu"
      >
        <button type="button" className={styles.addFieldBtn}>
          <PlusIcon />
          {t('record.import.v2.mapping.addField')}
        </button>
      </Dropdown>
    </div>
  )
}

export default ImportMappingStep
